import { useCanGoBack, useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

import { APP_ROUTES } from "@/components/router/routes";

const fillParams = (template: string, params?: Record<string, string | number>) =>
    template.replace(/:([A-Za-z0-9_]+)/g, (_, key) => {
        const val = params?.[key];
        if (val == null) throw new Error(`Missing route param "${key}"`);
        return encodeURIComponent(String(val));
    });

const toQuery = (path: string, query?: Record<string, string | number | boolean | null | undefined>) => {
    if (!query) return path;
    const u = new URL(path, "http://local");
    Object.entries(query).forEach(([k, v]) => v != null && u.searchParams.set(k, String(v)));
    return `${u.pathname}${u.search}`;
};

const getOriginSafe = () => (typeof window !== "undefined" ? window.location.origin : "");

export type CustomNavigateOptions = {
    params?: Record<string, string | number>;
    query?: Record<string, string | number | boolean | null | undefined>;
    replace?: boolean;
};

export type NavigateOptions = CustomNavigateOptions;

type UrlOptions = {
    params?: Record<string, string | number>;
    query?: Record<string, string | number | boolean | null | undefined>;
    external?: boolean;
};

export type RouterPaths = (typeof APP_ROUTES)[keyof typeof APP_ROUTES] | string;

export function useAppNavigation() {
    const navigate = useNavigate();
    const canGoBack = useCanGoBack();

    const navigateBack = useCallback(() => {
        if (canGoBack) {
            window.history.back();
        } else {
            navigate({ to: "/" });
        }
    }, [canGoBack, navigate]);

    const navigateTo = useCallback(
        (path: RouterPaths, options?: NavigateOptions) => {
            const actualPath = fillParams(path, options?.params);
            const finalPath = toQuery(actualPath, options?.query);

            navigate({
                to: finalPath,
                replace: options?.replace,
            });
        },
        [navigate]
    );

    const getURL = useCallback((path: RouterPaths, opts?: UrlOptions) => {
        const filled = fillParams(path, opts?.params);
        const withQ = toQuery(filled, opts?.query);
        return opts?.external ? `${getOriginSafe()}${withQ}` : withQ;
    }, []);

    return { navigateBack, navigate: navigateTo, getURL };
}
